import { Link } from '@tanstack/react-router';
import { Button, Card, Col, Flex, Result, Row, Skeleton, Typography } from 'antd';
import { useState } from 'react';
import { BackButton, BodyRender } from '../../../../components';
import { useSku } from '../../hooks';
import { formatPoints } from '../../utils';
import { AddToCartButton } from '../AddToCartButton/AddToCartButton';
import styles from './SkuDetail.module.scss';

const { Title, Text } = Typography;

type Props = {
  number: number;
};

const BackToMarket = () => (
  <Link to="/market">
    <Button type="primary">Back to market</Button>
  </Link>
);

export const SkuDetail = ({ number }: Props) => {
  const skuQuery = useSku(number);
  const [active, setActive] = useState(0);

  if (skuQuery.isPending) {
    return (
      <div>
        <Skeleton.Button active style={{ width: 110, marginBottom: 24 }} />
        <Row gutter={[32, 24]}>
          <Col xs={24} md={12}>
            <div className={styles.imageWrap}>
              <Skeleton.Image active />
            </div>
          </Col>
          <Col xs={24} md={12}>
            <Flex vertical gap={20}>
              <Skeleton active title={{ width: '70%' }} paragraph={{ rows: 1 }} />
              <Skeleton.Button active block style={{ height: 48 }} />
            </Flex>
          </Col>
        </Row>
      </div>
    );
  }

  if (skuQuery.data === null) {
    return (
      <Result
        status="404"
        title="Item not found"
        subTitle="It may have been removed or you used a stale link."
        extra={<BackToMarket />}
      />
    );
  }

  if (skuQuery.isError) {
    return (
      <Result
        status="error"
        title="Couldn't load this item"
        subTitle={skuQuery.error.message}
        extra={<BackToMarket />}
      />
    );
  }

  const sku = skuQuery.data;
  const images = sku.imageUrls;
  const main = images[active] ?? images[0] ?? null;

  return (
    <div>
      <BackButton fallbackTo="/market" />

      <Row gutter={[32, 24]}>
        <Col xs={24} md={12}>
          <div className={styles.imageWrap}>
            {main ? (
              <img src={main} alt={sku.title} className={styles.image} />
            ) : (
              <div className={styles.placeholder}>No image</div>
            )}
          </div>
          {images.length > 1 ? (
            <Flex gap={8} wrap className={styles.thumbs}>
              {images.map((url, i) => (
                <button
                  key={url}
                  type="button"
                  className={i === active ? `${styles.thumb} ${styles.thumbActive}` : styles.thumb}
                  onClick={() => setActive(i)}
                  aria-label={`${sku.title} image ${i + 1}`}
                >
                  <img src={url} alt="" loading="lazy" />
                </button>
              ))}
            </Flex>
          ) : null}
        </Col>

        <Col xs={24} md={12}>
          <Flex vertical gap={20} style={{ animation: 'fade-in var(--dur-normal) var(--ease-out) both' }}>
            <Title level={2} style={{ margin: 0 }}>
              {sku.title}
            </Title>
            <Text className={styles.price}>{formatPoints(sku.price)}</Text>
            <AddToCartButton sku={sku} size="large" block />
          </Flex>
        </Col>
      </Row>

      <Card className={styles.description} styles={{ body: { padding: 24 } }}>
        <Title level={4} style={{ marginTop: 0 }}>
          Description
        </Title>
        <BodyRender source={sku.description} />
      </Card>
    </div>
  );
};
